import React from 'react';

// Reusable confirmation dialog. Matches the app's modal idiom
// (fixed inset-0 overlay + white rounded-lg shadow-xl card, see
// EmailTemplates.js / Workspaces.js) and replaces the native window.confirm.
//
// Props:
//   isOpen        - render gate
//   title/message - dialog copy (message preserves line breaks)
//   confirmLabel  - primary button text
//   cancelLabel   - secondary button text
//   onConfirm     - primary action (may be async)
//   onCancel      - dismiss handler (also fires on backdrop click / Escape)
//   busy          - shows a spinner and locks both buttons + backdrop
//   tone          - 'primary' (default) | 'danger' for destructive actions
const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  busy = false,
  tone = 'primary',
}) => {
  if (!isOpen) return null;

  const confirmClass = tone === 'danger'
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-primary hover:bg-primary/90';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"
      onClick={busy ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 className="text-xl font-bold text-gray-800 mb-2">{title}</h2>}
        {message && <p className="text-sm text-gray-600 mb-6 whitespace-pre-line">{message}</p>}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 text-white rounded flex items-center gap-2 disabled:opacity-50 ${confirmClass}`}
          >
            {busy && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
